import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faQrcode,
  faChartLine,
  faComments,
  faEye,
  faFlask,
  faArrowUp,
  faArrowDown,
  faArrowRight,
  faMinus,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import { useQueryState } from "nuqs"
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
} from "recharts"
import ApiError from "../../components/ApiError"
import { useVideoScanDashboard } from "../../hooks/useVideoLogs"
import {
  useDateRangeFilter,
  CUSTOM_RANGE,
} from "../../hooks/useDateRangeFilter"
import routePaths from "../../constants/path"
import { sourceOptions } from "../../constants"
import DateRangeControl from "../../components/DateRangeControl"
import Dropdown from "../../components/Dropdown"

const DEFAULT_RANGE = "This Month"
const ALL_SOURCES = "All"

// Business targets, not derived from scan data — adjust here as goals change.
const SCAN_RATE_TARGET = 0.05
const FEEDBACK_SUBMITTED_TARGET_RATE = 0.05

const BLUE = "#2563eb"
const GREEN = "#16a34a"
const RED = "#dc2626"

const tileAccents = {
  blue: "bg-secondary/10 text-secondary_dark",
  indigo: "bg-indigo-500/10 text-indigo-600",
  green: "bg-green-500/10 text-green-600",
  red: "bg-red-500/10 text-red-600",
}

const accentBars = {
  blue: "bg-secondary_dark",
  indigo: "bg-indigo-500",
  green: "bg-green-500",
  red: "bg-red-500",
}

const StatTile = ({
  label,
  value,
  valueClassName = "text-gray-900",
  sublabel,
  target,
  targetClassName = "text-red-500",
  icon,
  accent = "blue",
  to,
}) => {
  const Wrapper = to ? Link : "div"
  return (
    <Wrapper
      className={`group relative flex flex-1 min-w-[210px] items-center gap-3.5 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${
        to ? "cursor-pointer hover:border-secondary_dark/40" : ""
      }`}
      title={to ? "View in Customer Feedback" : undefined}
      {...(to ? { to } : {})}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${accentBars[accent]}`} />
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tileAccents[accent]}`}
      >
        <FontAwesomeIcon className="h-4.5 w-4.5" icon={icon} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          {label}
        </div>
        <div
          className={`mt-0.5 text-2xl font-bold leading-none tabular-nums ${valueClassName}`}
        >
          {value}
        </div>
        {sublabel && (
          <div className="mt-1 text-xs text-gray-500">{sublabel}</div>
        )}
        {target && (
          <div className={`mt-1 text-xs font-semibold ${targetClassName}`}>
            {target}
          </div>
        )}
      </div>
      {to && (
        <FontAwesomeIcon
          className="absolute right-3 top-3 h-3 w-3 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100"
          icon={faArrowRight}
        />
      )}
    </Wrapper>
  )
}

// The report-generation service picks this copy per variant and doesn't
// store it anywhere this repo can read — hardcoded here purely as a display
// label so the card says what each variant actually shows, not just its
// letter. Update this if that service's copy changes.
const QR_HEADING_VARIANT_COPY = {
  A: "Understanding Your Report Findings with Videos",
  B: "What does this actually mean for me?",
}

// Side-by-side variant columns rather than a single number, so this is its
// own card instead of a StatTile — a scan-rate or total-scans figure split
// across two small annotations is harder to compare at a glance than the two
// variants sitting right next to each other. Only rendered once the
// experiment has assignments to show — see getExperimentVariantBreakdown,
// which zero-fills both variants as soon as the experiment is enabled.
//
// A proportion bar reads at a glance which variant is ahead, which a column
// of numbers doesn't — ties each variant's share of the bar directly to its
// share of the legend below. Colors are assigned by position (1st/2nd
// variant), not by letter, since VariantValue is free text once more than
// two variants exist.
const VARIANT_BAR_COLORS = ["bg-purple-500", "bg-teal-500", "bg-amber-500"]
const VARIANT_DOT_COLORS = ["bg-purple-500", "bg-teal-500", "bg-amber-500"]

const VariantComparisonCard = ({ breakdown }) => {
  const totalScans = breakdown.reduce((sum, row) => sum + row.TotalScans, 0)
  const hasScans = totalScans > 0

  return (
    <div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <FontAwesomeIcon className="h-4 w-4 text-purple-600" icon={faFlask} />
        <h2 className="text-sm font-semibold text-gray-900">
          Scans by QR heading variant
        </h2>
      </div>

      {/* An even split only when there are real scans to split — at 0/0 a
          colored 50/50 bar would read as a tie instead of "no data yet". */}
      <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100">
        {hasScans &&
          breakdown.map((row, index) => {
            const share = (row.TotalScans / totalScans) * 100
            return (
              <div
                key={row.VariantValue}
                className={
                  VARIANT_BAR_COLORS[index % VARIANT_BAR_COLORS.length]
                }
                style={{ width: `${share}%` }}
              />
            )
          })}
      </div>

      <div className="mt-3.5 flex flex-wrap justify-between gap-4">
        {breakdown.map((row, index) => {
          const share = totalScans ? (row.TotalScans / totalScans) * 100 : 0
          return (
            <div key={row.VariantValue} className="flex min-w-[140px] gap-2">
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                  VARIANT_DOT_COLORS[index % VARIANT_DOT_COLORS.length]
                }`}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  Variant {row.VariantValue} · {row.TotalScans.toLocaleString()}{" "}
                  scans ({share.toFixed(0)}%)
                </div>
                <div className="text-xs text-gray-500">
                  {row.UniqueScans.toLocaleString()} unique
                </div>
                <div
                  className="mt-0.5 truncate text-xs text-gray-400"
                  title={QR_HEADING_VARIANT_COPY[row.VariantValue]}
                >
                  {QR_HEADING_VARIANT_COPY[row.VariantValue] || "—"}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-400">
        {breakdown
          .map(
            (row) => `${row.TotalScans.toLocaleString()} (${row.VariantValue})`
          )
          .join(" + ")}{" "}
        = {totalScans.toLocaleString()} total scans
      </div>
    </div>
  )
}

const StatTileSkeleton = () => (
  <div className="h-[84px] flex-1 min-w-[210px] animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
)

const ChartCardSkeleton = ({ height }) => (
  <div
    className="flex-1 min-w-[320px] animate-pulse rounded-2xl border border-gray-100 bg-gray-50"
    style={{ height: height + 48 }}
  />
)

const SectionLabel = ({ children }) => (
  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
    {children}
  </p>
)

const ChartCard = ({ title, isEmpty, emptyLabel, height, children }) => (
  <div className="flex-1 min-w-[320px] rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <h2 className="mb-3 text-sm font-semibold text-gray-900">{title}</h2>
    {isEmpty ? (
      <div
        className="flex items-center justify-center text-sm text-gray-400"
        style={{ height }}
      >
        {emptyLabel}
      </div>
    ) : (
      children
    )}
  </div>
)

const TREND_STYLES = {
  up: { icon: faArrowUp, className: "text-green-700", bg: "bg-green-50" },
  down: { icon: faArrowDown, className: "text-red-700", bg: "bg-red-50" },
  flat: { icon: faMinus, className: "text-gray-500", bg: "bg-gray-100" },
}

// "new" (no prior-period rate to compare against) is its own state, not a
// missing case of up/down — it never carries a directional arrow, regardless
// of what `trend` says, so a stale cache or a future bug in the trend field
// can't make this render as a contradiction (e.g. a down arrow next to "new").
//
// Otherwise, the badge shows the plain percentage-point difference between
// the two periods' Rate ("15% -> 20%" is "+5pp") rather than a relative %
// change — relative-% explodes when the previous rate is small (1.7% -> 10.9%
// would read as "+535%" for a fairly modest absolute move), which is far more
// dramatic-looking than the underlying change actually is. pp stays legible
// regardless of how small the base rate was.
const TrendBadge = ({ trend, rateChangePp }) => {
  if (rateChangePp === null || rateChangePp === undefined) {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600">
        new
      </span>
    )
  }

  const { icon, className, bg } = TREND_STYLES[trend] || TREND_STYLES.flat

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${bg} ${className}`}
    >
      <FontAwesomeIcon className="h-2.5 w-2.5" icon={icon} />
      {Math.abs(rateChangePp).toFixed(1)}pp
    </span>
  )
}

const TableHeaderCell = ({ children, align = "text-left" }) => (
  <th
    className={`px-3 py-2 ${align} text-[10px] font-semibold uppercase tracking-wider text-gray-500`}
  >
    {children}
  </th>
)

const TableCell = ({ children, align = "text-left", className = "" }) => (
  <td className={`px-3 py-2.5 text-sm ${align} ${className}`}>{children}</td>
)

const ChartTooltip = ({
  active,
  payload,
  label,
  valueLabel = "Scans",
  labelFormatter,
}) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-0.5 font-semibold text-gray-900">
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      <div className="text-gray-600">
        {valueLabel}: <span className="font-semibold">{payload[0].value}</span>
      </div>
    </div>
  )
}

const VideoScanDashboard = () => {
  const {
    dateRange,
    customFrom,
    customTo,
    isCustomRange,
    start,
    end,
    rangeLabel,
    triggerLabel,
    presetOptions,
    getCustomSeed,
    handleSelectPreset,
    handleApplyCustom,
    handleResetFilters: handleResetDateRange,
  } = useDateRangeFilter({ defaultRange: DEFAULT_RANGE })
  const timePeriod = start || end ? [start, end].join(",") : undefined
  const [source, setSource] = useQueryState("source", {
    defaultValue: ALL_SOURCES,
  })

  const handleResetFilters = () => {
    handleResetDateRange()
    setSource(ALL_SOURCES)
  }

  const {
    data: {
      totals,
      reportsIssued,
      facilityBreakdown,
      bodyPartBreakdown,
      dailyVolume,
      centerScanRates,
      specialtyBreakdown,
      orderingPhysicianStudyBreakdown,
      orderingPhysicianScanBreakdown,
      experimentVariantBreakdown,
    } = {},
    isLoading,
    isError,
    error,
  } = useVideoScanDashboard({
    timePeriod,
    source: source === ALL_SOURCES ? undefined : source,
  })

  if (isError) {
    return (
      <div className="mx-auto mt-4 max-w-[1400px] px-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  const reportsScanned = totals?.ReportsScanned || 0
  const scanRate = reportsIssued > 0 ? reportsScanned / reportsIssued : 0
  const isScanRateBelowTarget = scanRate < SCAN_RATE_TARGET
  const totalScans = totals?.TotalScans || 0
  const feedbackSubmitted = totals?.FeedbackSubmitted || 0
  const feedbackSubmittedTarget = Math.round(
    totalScans * FEEDBACK_SUBMITTED_TARGET_RATE
  )
  const isFeedbackBelowTarget = feedbackSubmitted < feedbackSubmittedTarget

  // Carries this page's date range and source over so "Feedback submitted"
  // opens Customer Feedback already scoped the same way, not "This Month".
  const feedbackLinkParams = new URLSearchParams({ dateRange })
  if (isCustomRange) {
    if (customFrom) feedbackLinkParams.set("from", customFrom)
    if (customTo) feedbackLinkParams.set("to", customTo)
  }
  if (source !== ALL_SOURCES) feedbackLinkParams.set("source", source)
  const feedbackLink = `${routePaths.customerFeedback}?${feedbackLinkParams.toString()}`

  const facilityChartHeight = Math.max(
    (facilityBreakdown?.length || 0) * 32,
    180
  )
  const bodyPartChartHeight = Math.max(
    (bodyPartBreakdown?.length || 0) * 32,
    180
  )
  // Physician names run longer than facility/body-part labels and wrap
  // across two lines at this axis width, so each row needs more height.
  const orderingPhysicianStudyChartHeight = Math.max(
    (orderingPhysicianStudyBreakdown?.length || 0) * 40,
    180
  )
  const orderingPhysicianScanChartHeight = Math.max(
    (orderingPhysicianScanBreakdown?.length || 0) * 40,
    180
  )

  const hasActiveFilters = dateRange !== DEFAULT_RANGE || source !== ALL_SOURCES

  return (
    <div className="mx-auto max-w-[90%] px-4 pb-6 pt-2 sm:px-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-primary">
            Scan Dashboard
          </h1>
        </div>

        <div className="flex items-end gap-2">
          <Dropdown
            label="SOURCE"
            options={[ALL_SOURCES, ...sourceOptions]}
            setValue={setSource}
            title="Filter by report source"
            value={source}
          />
          <DateRangeControl
            customLabel={CUSTOM_RANGE}
            getCustomSeed={getCustomSeed}
            presets={presetOptions}
            triggerLabel={triggerLabel}
            value={dateRange}
            onApplyCustom={handleApplyCustom}
            onSelectPreset={handleSelectPreset}
          />
          <button
            className="rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={!hasActiveFilters}
            title="Reset to This Month and all sources"
            type="button"
            onClick={handleResetFilters}
          >
            Reset
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <ChartCardSkeleton height={100} />
          <div className="flex flex-wrap gap-3">
            <StatTileSkeleton />
            <StatTileSkeleton />
            <StatTileSkeleton />
            <StatTileSkeleton />
          </div>
          <div className="flex flex-wrap gap-3">
            <ChartCardSkeleton height={220} />
            <ChartCardSkeleton height={220} />
          </div>
          <ChartCardSkeleton height={260} />
          <div className="flex flex-wrap gap-3">
            <ChartCardSkeleton height={220} />
            <ChartCardSkeleton height={220} />
          </div>
          <div className="flex flex-wrap gap-3">
            <ChartCardSkeleton height={160} />
            <ChartCardSkeleton height={160} />
          </div>
        </div>
      ) : (
        <>
          {experimentVariantBreakdown?.length > 0 && (
            <div className="mb-5">
              <VariantComparisonCard breakdown={experimentVariantBreakdown} />
            </div>
          )}

          <div className="mb-5 flex flex-wrap gap-3">
            <StatTile
              accent={isScanRateBelowTarget ? "red" : "green"}
              icon={faChartLine}
              label="Scan rate"
              target={`Target: ${(SCAN_RATE_TARGET * 100).toFixed(0)}%`}
              targetClassName={
                isScanRateBelowTarget ? "text-red-500" : "text-green-600"
              }
              value={`${(scanRate * 100).toFixed(2)}%`}
              valueClassName={
                isScanRateBelowTarget ? "text-red-600" : "text-gray-900"
              }
            />
            <StatTile
              accent="blue"
              icon={faQrcode}
              label="Reports scanned"
              sublabel={`of ${reportsIssued.toLocaleString()} issued`}
              value={reportsScanned.toLocaleString()}
            />
            <StatTile
              accent="indigo"
              icon={faEye}
              label="Total scans"
              sublabel={`${(totals?.UniqueScans || 0).toLocaleString()} unique`}
              value={totalScans.toLocaleString()}
            />
            <StatTile
              accent={isFeedbackBelowTarget ? "red" : "green"}
              icon={faComments}
              label="Feedback submitted"
              target={`Target: ~${feedbackSubmittedTarget} (5% of scans)`}
              targetClassName={
                isFeedbackBelowTarget ? "text-red-500" : "text-green-600"
              }
              to={feedbackLink}
              value={feedbackSubmitted.toLocaleString()}
              valueClassName={
                isFeedbackBelowTarget ? "text-red-600" : "text-gray-900"
              }
            />
          </div>

          <SectionLabel>Breakdowns</SectionLabel>
          <div className="mb-5 flex flex-wrap gap-3">
            <ChartCard
              emptyLabel="No scans recorded for these filters."
              height={facilityChartHeight}
              isEmpty={!facilityBreakdown?.length}
              title="Scans by facility (top 10)"
            >
              <ResponsiveContainer height={facilityChartHeight} width="100%">
                <BarChart
                  data={facilityBreakdown}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                >
                  <CartesianGrid horizontal={false} stroke="#e5e7eb" />
                  <XAxis
                    allowDecimals={false}
                    axisLine={false}
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="number"
                  />
                  <YAxis
                    axisLine={false}
                    dataKey="Facility"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="category"
                    width={110}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: "#f3f4f6" }}
                  />
                  <Bar dataKey="TotalScans" fill={BLUE} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              emptyLabel="No scans recorded for these filters."
              height={bodyPartChartHeight}
              isEmpty={!bodyPartBreakdown?.length}
              title="Scans by body part"
            >
              <ResponsiveContainer height={bodyPartChartHeight} width="100%">
                <BarChart
                  data={bodyPartBreakdown}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                >
                  <CartesianGrid horizontal={false} stroke="#e5e7eb" />
                  <XAxis
                    allowDecimals={false}
                    axisLine={false}
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="number"
                  />
                  <YAxis
                    axisLine={false}
                    dataKey="BodyPart"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="category"
                    width={110}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: "#f3f4f6" }}
                  />
                  <Bar dataKey="TotalScans" radius={[0, 4, 4, 0]}>
                    {bodyPartBreakdown?.map((entry) => (
                      <Cell
                        key={entry.BodyPart}
                        fill={entry.BodyPart === "(unmapped)" ? RED : GREEN}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="mb-5">
            <ChartCard
              emptyLabel="No scans recorded for these filters."
              height={260}
              isEmpty={!dailyVolume?.length}
              title={
                rangeLabel
                  ? `Daily scan volume (${rangeLabel})`
                  : "Daily scan volume"
              }
            >
              <ResponsiveContainer height={260} width="100%">
                <AreaChart
                  data={dailyVolume}
                  margin={{ top: 8, right: 16, bottom: 4, left: 4 }}
                >
                  <defs>
                    <linearGradient
                      id="dailyVolumeFill"
                      x1="0"
                      x2="0"
                      y1="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={BLUE} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    axisLine={false}
                    dataKey="Date"
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(iso) =>
                      DateTime.fromISO(iso).toFormat("M/d")
                    }
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    width={32}
                  />
                  <Tooltip
                    content={
                      <ChartTooltip
                        labelFormatter={(iso) =>
                          DateTime.fromISO(iso).toFormat("MMM d, yyyy")
                        }
                        valueLabel="Scans"
                      />
                    }
                  />
                  <Area
                    dataKey="TotalScans"
                    dot={{ r: 3, fill: BLUE, strokeWidth: 0 }}
                    fill="url(#dailyVolumeFill)"
                    stroke={BLUE}
                    strokeWidth={2}
                    type="monotone"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <SectionLabel>Top ordering doctors</SectionLabel>
          <div className="mb-5 flex flex-wrap gap-3">
            <ChartCard
              emptyLabel="No ordering-physician data for these filters."
              height={orderingPhysicianStudyChartHeight}
              isEmpty={!orderingPhysicianStudyBreakdown?.length}
              title="Top ordering doctors (by studies)"
            >
              <ResponsiveContainer
                height={orderingPhysicianStudyChartHeight}
                width="100%"
              >
                <BarChart
                  data={orderingPhysicianStudyBreakdown}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                >
                  <CartesianGrid horizontal={false} stroke="#e5e7eb" />
                  <XAxis
                    allowDecimals={false}
                    axisLine={false}
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="number"
                  />
                  <YAxis
                    axisLine={false}
                    dataKey="PhysicianName"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="category"
                    width={200}
                  />
                  <Tooltip
                    content={<ChartTooltip valueLabel="Studies" />}
                    cursor={{ fill: "#f3f4f6" }}
                  />
                  <Bar dataKey="TotalStudies" radius={[0, 4, 4, 0]}>
                    {orderingPhysicianStudyBreakdown?.map((entry) => (
                      <Cell
                        key={entry.PhysicianName}
                        fill={entry.PhysicianName === "(unknown)" ? RED : BLUE}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              emptyLabel="No ordering-physician data for these filters."
              height={orderingPhysicianScanChartHeight}
              isEmpty={!orderingPhysicianScanBreakdown?.length}
              title="Top ordering doctors (by scans)"
            >
              <ResponsiveContainer
                height={orderingPhysicianScanChartHeight}
                width="100%"
              >
                <BarChart
                  data={orderingPhysicianScanBreakdown}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                >
                  <CartesianGrid horizontal={false} stroke="#e5e7eb" />
                  <XAxis
                    allowDecimals={false}
                    axisLine={false}
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="number"
                  />
                  <YAxis
                    axisLine={false}
                    dataKey="PhysicianName"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    type="category"
                    width={200}
                  />
                  <Tooltip
                    content={<ChartTooltip valueLabel="Scans" />}
                    cursor={{ fill: "#f3f4f6" }}
                  />
                  <Bar dataKey="TotalScans" radius={[0, 4, 4, 0]}>
                    {orderingPhysicianScanBreakdown?.map((entry) => (
                      <Cell
                        key={entry.PhysicianName}
                        fill={entry.PhysicianName === "(unknown)" ? RED : GREEN}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <SectionLabel>Leaderboards</SectionLabel>
          <div className="flex flex-wrap gap-3">
            <ChartCard
              emptyLabel="No issuer has both issued and scanned reports for these filters."
              height={160}
              isEmpty={!centerScanRates?.length}
              title="Top centers by scan rate"
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr>
                      <TableHeaderCell>Issuer</TableHeaderCell>
                      <TableHeaderCell align="text-right">
                        Issued Reports
                      </TableHeaderCell>
                      <TableHeaderCell align="text-right">Rate</TableHeaderCell>
                      <TableHeaderCell align="text-right">
                        Trend
                      </TableHeaderCell>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {centerScanRates?.map((row) => (
                      <tr
                        key={row.PatientIssuer}
                        className="transition-colors hover:bg-gray-50"
                      >
                        <TableCell className="font-medium text-gray-900">
                          {row.PatientIssuer}
                        </TableCell>
                        <TableCell
                          align="text-right"
                          className="tabular-nums text-gray-500"
                        >
                          {row.ReportsIssued.toLocaleString()}
                        </TableCell>
                        <TableCell
                          align="text-right"
                          className="tabular-nums text-gray-700"
                        >
                          {(row.Rate * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell align="text-right">
                          <TrendBadge
                            rateChangePp={row.RateChangePp}
                            trend={row.Trend}
                          />
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            <ChartCard
              emptyLabel="No matched-physician specialty data for these filters."
              height={160}
              isEmpty={!specialtyBreakdown?.length}
              title="Top specialties scanning"
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr>
                      <TableHeaderCell>Specialty</TableHeaderCell>
                      <TableHeaderCell align="text-right">
                        Scans
                      </TableHeaderCell>
                      <TableHeaderCell align="text-right">
                        Physicians
                      </TableHeaderCell>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {specialtyBreakdown?.map((row) => (
                      <tr
                        key={row.Specialty}
                        className="transition-colors hover:bg-gray-50"
                      >
                        <TableCell className="font-medium text-gray-900">
                          {row.Specialty}
                        </TableCell>
                        <TableCell
                          align="text-right"
                          className="tabular-nums text-gray-700"
                        >
                          {row.Scans.toLocaleString()}
                        </TableCell>
                        <TableCell
                          align="text-right"
                          className="tabular-nums text-gray-700"
                        >
                          {row.Physicians.toLocaleString()}
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  )
}

export default VideoScanDashboard
